import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const { 
  FiCpu, FiCode, FiActivity, FiLayers, FiDatabase, FiShield, 
  FiChevronRight, FiBox, FiTerminal, FiZap, FiSearch, FiGlobe 
} = FiIcons;

export default function AIArchitect() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate AI Analysis Deep-Scan
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { label: 'System Health', val: '98%', icon: FiShield, color: 'text-emerald-400' },
    { label: 'Data Nodes', val: '24', icon: FiDatabase, color: 'text-indigo-400' },
    { label: 'Logic Complexity', val: 'Low', icon: FiActivity, color: 'text-amber-400' },
    { label: 'API Latency', val: '12ms', icon: FiZap, color: 'text-purple-400' },
  ];

  if (loading) return (
    <div className="h-[80vh] flex flex-col items-center justify-center space-y-6">
      <div className="relative">
        <div className="w-24 h-24 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        <SafeIcon icon={FiCpu} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl text-indigo-400 animate-pulse" />
      </div>
      <p className="text-slate-400 font-mono text-sm animate-pulse uppercase tracking-[0.3em]">Deep Scanning Codebase...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto pb-20 space-y-10">
      {/* Hero Section */}
      <header className="relative p-12 rounded-[3rem] overflow-hidden border border-white/10 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 backdrop-blur-3xl">
        <div className="absolute top-0 right-0 p-8">
           <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
             <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
             <span className="text-[10px] font-black text-white uppercase tracking-widest">Model: Architect-v2.1</span>
           </div>
        </div>
        
        <div className="relative z-10 space-y-4">
          <h1 className="text-6xl font-black text-white tracking-tighter uppercase leading-none">
            System <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Intelligence</span>
          </h1>
          <p className="text-slate-400 max-w-xl text-lg font-medium">
            Autonomous codebase analysis, dependency mapping, and architectural documentation.
          </p>
        </div>
      </header>

      {/* Bento Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            key={i} 
            className="bg-white/[0.02] border border-white/10 p-8 rounded-[2rem] hover:border-white/20 transition-all group"
          >
            <div className={`w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-6 ${stat.color} group-hover:scale-110 transition-transform`}>
              <SafeIcon icon={stat.icon} className="text-xl" />
            </div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{stat.label}</p>
            <h3 className="text-3xl font-black text-white tracking-tight">{stat.val}</h3>
          </motion.div>
        ))}
      </div>

      {/* Main Analysis View */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-3 space-y-2">
          {[
            { id: 'overview', label: 'Overview', icon: FiGlobe },
            { id: 'components', label: 'Components', icon: FiBox },
            { id: 'dataflow', label: 'Data Flow', icon: FiActivity },
            { id: 'security', label: 'Security', icon: FiShield },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all text-sm font-bold ${
                activeTab === item.id 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                : 'text-slate-500 hover:bg-white/5 hover:text-slate-300'
              }`}
            >
              <SafeIcon icon={item.icon} />
              {item.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="lg:col-span-9">
          <div className="bg-white/[0.02] border border-white/10 rounded-[2.5rem] p-10 min-h-[500px] backdrop-blur-xl">
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="space-y-4">
                    <h2 className="text-2xl font-black text-white uppercase tracking-tight">Architectural Summary</h2>
                    <div className="h-1 w-20 bg-indigo-500 rounded-full" />
                  </div>
                  
                  <div className="prose prose-invert max-w-none">
                    <p className="text-slate-400 leading-relaxed text-lg">
                      The core architecture follows a <span className="text-white font-bold">Modular Monolith</span> pattern. 
                      State is managed via React hooks with persistent storage in Supabase. 
                      The UI layer is decoupled using a specialized <span className="text-indigo-400 font-mono">SafeIcon</span> abstraction to ensure library stability.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-6 bg-white/5 rounded-3xl border border-white/5">
                      <h4 className="text-white font-black text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
                        <SafeIcon icon={FiCode} className="text-indigo-400" /> Key Strengths
                      </h4>
                      <ul className="space-y-3">
                        {['Clean separation of concerns', 'Robust RLS security policies', 'High-performance UI thread'].map((s, i) => (
                          <li key={i} className="text-sm text-slate-400 flex items-center gap-3">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-6 bg-white/5 rounded-3xl border border-white/5">
                      <h4 className="text-white font-black text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
                        <SafeIcon icon={FiActivity} className="text-amber-400" /> Optimization Targets
                      </h4>
                      <ul className="space-y-3">
                        {['Reduce useEffect dependency arrays', 'Implement query caching', 'Consolidate redundant migrations'].map((s, i) => (
                          <li key={i} className="text-sm text-slate-400 flex items-center gap-3">
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'components' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center mb-10">
                    <h2 className="text-2xl font-black text-white uppercase">Module Breakdown</h2>
                    <div className="flex gap-2">
                       <button className="p-2 bg-white/5 rounded-lg text-slate-400 hover:text-white transition-colors"><SafeIcon icon={FiSearch} /></button>
                       <button className="p-2 bg-white/5 rounded-lg text-slate-400 hover:text-white transition-colors"><SafeIcon icon={FiTerminal} /></button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      { name: 'CRM.jsx', type: 'View', lines: 420, status: 'Stable' },
                      { name: 'Sessions.jsx', type: 'Controller', lines: 850, status: 'Complex' },
                      { name: 'Supabase.js', type: 'Service', lines: 45, status: 'Critical' },
                      { name: 'Sidebar.jsx', type: 'Layout', lines: 180, status: 'Optimized' },
                    ].map((comp, i) => (
                      <div key={i} className="flex items-center justify-between p-6 bg-white/[0.03] border border-white/5 rounded-2xl hover:bg-white/[0.06] transition-all cursor-pointer group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                             <SafeIcon icon={FiBox} />
                          </div>
                          <div>
                            <p className="text-sm font-black text-white group-hover:text-indigo-400 transition-colors uppercase tracking-tight">{comp.name}</p>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{comp.type} • {comp.lines} LOC</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                           <span className={`text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${comp.status === 'Critical' ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-slate-400'}`}>
                             {comp.status}
                           </span>
                           <SafeIcon icon={FiChevronRight} className="text-slate-600 group-hover:text-white transition-all" />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}